/**
 * ENT-5492 (D3): the process-wide "this session has ended" latch.
 *
 * Set exactly once by the unified session-expiry path in
 * `protectedFunctionAuth.ts` when a protected call is TERMINALLY
 * unauthenticated (the refresh of the stored session failed). While it is set,
 * callers whose request failed because the session died stay silent — the one
 * in-place "Your session ended" notice on `/auth` is the only message the user
 * sees. Cleared whenever a valid session is (re-)established.
 *
 * Deliberately a dependency-free leaf module so the toast primitives can read
 * it without importing the Supabase client.
 */
let sessionEnded = false;
let sessionEndedAt = 0;

export const SESSION_ENDED_COPY = {
  title: 'Your session ended',
  description: 'Sign in to continue where you left off.',
} as const;

/**
 * How long after the session ended errors are still silenced on the auth
 * route: long enough for requests that were in flight when the session died to
 * settle after the redirect, far shorter than anyone re-typing a password. After
 * it, an error on `/auth` is the user's own sign-in attempt and is shown
 * (Codex P2 on PR #3532). Off the auth route, the dying page stays silent.
 */
export const SESSION_ENDED_AUTH_GRACE_MS = 3_000;

const sessionEndedListeners = new Set<() => void>();

/** Latch the ended state. Returns true only for the call that set it. */
export function markSessionEnded(): boolean {
  if (sessionEnded) {
    return false;
  }
  sessionEnded = true;
  sessionEndedAt = Date.now();
  // In-process: a page already mounted in THIS tab (browsers never send a
  // `storage` event back to the writing tab).
  // A listener can never stop the latch or the sign-out that follows it.
  sessionEndedListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error('Session-ended listener failed:', error);
    }
  });
  return true;
}

export function clearSessionEnded(): void {
  sessionEnded = false;
  sessionEndedAt = 0;
}

export function isSessionEnded(): boolean {
  return sessionEnded;
}

/**
 * Cross-tab reason (Codex round 2 on PR #3532). The auth library broadcasts a
 * sign-out to every tab but not WHY it happened. The tab that owns an
 * involuntary sign-out publishes the reason here BEFORE it signs out; every
 * other tab adopts it when it processes that SIGNED_OUT. A voluntary sign-out
 * never publishes it, so it never shows "Your session ended". Bounded by a
 * horizon (a stale reason is never adopted) and forgotten on the next sign-in.
 * Not an app key of `clearLocalAuthState`: clearing it there would race the
 * other tabs reading it.
 */
export const SESSION_ENDED_STORAGE_KEY = 'sg:session-ended';
export const SESSION_ENDED_BROADCAST_HORIZON_MS = 60_000;

export function publishSessionEndedAcrossTabs(now: number = Date.now()): void {
  try {
    localStorage.setItem(SESSION_ENDED_STORAGE_KEY, JSON.stringify({ at: now }));
  } catch {
    // Storage unavailable: other tabs fall back to their own 401 path.
  }
}

/** On a SIGNED_OUT from another tab: latch only when the reason is fresh. */
export function adoptSessionEndedFromOtherTab(now: number = Date.now()): boolean {
  try {
    const raw = localStorage.getItem(SESSION_ENDED_STORAGE_KEY);
    const at = raw ? (JSON.parse(raw) as { at?: unknown }).at : undefined;
    if (typeof at !== 'number' || now - at < 0 || now - at > SESSION_ENDED_BROADCAST_HORIZON_MS) {
      return false;
    }
  } catch {
    return false;
  }
  markSessionEnded();
  return true;
}

/**
 * The library may broadcast SIGNED_OUT before the ending tab publishes its
 * reason (a dead refresh token is removed inside `getSession`/`refreshSession`),
 * so a tab already showing /auth also listens for the reason itself; a page
 * in the tab that ends the session hears it in-process.
 */
export function subscribeToSessionEnded(onEnded: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === SESSION_ENDED_STORAGE_KEY && event.newValue) {
      // Adopting latches this tab, which notifies through the in-process set.
      adoptSessionEndedFromOtherTab();
    }
  };
  sessionEndedListeners.add(onEnded);
  window.addEventListener('storage', onStorage);
  return () => {
    sessionEndedListeners.delete(onEnded);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * The other half of telling sign-outs apart (ENT-5492 AC5): the auth library
 * also signs a session out ON ITS OWN when GoTrue says it is dead (`getUser` →
 * `session_not_found`, a refused refresh token) and emits a SIGNED_OUT that
 * carries no reason. Every app-initiated sign-out marks the session it ends
 * (its `session_id` claim) here first, so a SIGNED_OUT that ends a session
 * nobody marked is the library ending a dead one. Keyed by session, so a mark
 * left by an earlier sign-out never covers the next session (Codex P2 on
 * PR #3573); an unknown session is never treated as voluntary. Shared across
 * tabs, bounded by the same horizon.
 */
export const VOLUNTARY_SIGN_OUT_STORAGE_KEY = 'sg:signed-out-voluntarily';

// This tab's own mark, kept in memory too: storage can be unavailable (hardening F4).
let ownVoluntarySignOut: { at: number; sessionId: string | null } | null = null;

export function publishVoluntarySignOut(sessionId: string | null, now: number = Date.now()): void {
  ownVoluntarySignOut = { at: now, sessionId };
  try {
    localStorage.setItem(VOLUNTARY_SIGN_OUT_STORAGE_KEY, JSON.stringify({ at: now, sessionId }));
  } catch {
    // Storage unavailable: the sign-out may read as involuntary (a notice, not a lockout).
  }
}

/**
 * An app sign-out that FAILED left its session live: withdraw the mark, so a
 * later ending of that same session is never read as voluntary (Codex round 2
 * P2 on PR #3573).
 */
export function withdrawVoluntarySignOut(): void {
  ownVoluntarySignOut = null;
  try {
    localStorage.removeItem(VOLUNTARY_SIGN_OUT_STORAGE_KEY);
  } catch {
    // Storage unavailable: the horizon still expires the mark.
  }
}

/** The ending of a dead session also signs out through the library: not voluntary. */
export function publishVoluntarySignOutUnlessSessionEnded(sessionId: string | null): void {
  if (!sessionEnded) {
    publishVoluntarySignOut(sessionId);
  }
}

/** Whether THIS session was signed out by the app, recently. */
export function isVoluntarySignOutOf(sessionId: string | null, now: number = Date.now()): boolean {
  if (!sessionId) {
    return false;
  }
  const marks = (mark: { at?: unknown; sessionId?: unknown } | null): boolean => {
    const at = mark?.at;
    return typeof at === 'number'
      && now - at >= 0
      && now - at <= SESSION_ENDED_BROADCAST_HORIZON_MS
      && mark?.sessionId === sessionId;
  };
  if (marks(ownVoluntarySignOut)) {
    return true;
  }
  try {
    const raw = localStorage.getItem(VOLUNTARY_SIGN_OUT_STORAGE_KEY);
    return marks(raw ? (JSON.parse(raw) as { at?: unknown; sessionId?: unknown }) : null);
  } catch {
    return false;
  }
}

export function forgetSessionEndedAcrossTabs(): void {
  try {
    localStorage.removeItem(SESSION_ENDED_STORAGE_KEY);
  } catch {
    // Storage unavailable: the horizon still expires the reason.
  }
}

function isAuthRoute(pathname: string): boolean {
  return pathname === '/auth' || pathname.startsWith('/auth/') || pathname === '/admin/sign-in';
}

/**
 * Whether an error raised now belongs to the requests that died with the
 * session (silence it) rather than to the user's re-authentication (show it).
 */
export function shouldSilenceSessionEndedErrors(
  pathname: string = typeof window === 'undefined' ? '' : window.location.pathname,
  now: number = Date.now(),
): boolean {
  if (!sessionEnded) {
    return false;
  }
  if (!isAuthRoute(pathname)) {
    return true;
  }
  return now - sessionEndedAt < SESSION_ENDED_AUTH_GRACE_MS;
}
